import { useNavigate } from "@vixi/router";
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import { courseBaseByIdQueryOptions, courseByIdQueryOptions, useBasicEditCourse } from "@vixi/api";
import {
  Button,
  CourseDetailsForm,
  DemographicFormValues,
  demographicValidationSchema,
  MainDetailsFormValues,
  mainDetailsValidationSchema,
} from "@vixi/ui";
import { EditBasicCourseRequest } from "@vixi/types";
import { assert } from "@vixi/utils";

const routeId = "/_app/courses/$courseId/edit-info" satisfies RouterIds;
const Route = router.routesById[routeId];

export const EditCourseInfoPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { courseId } }) => {
    queryClient.ensureQueryData(courseBaseByIdQueryOptions(queryClient, courseId));
  },
};

function RouteComponent() {
  const { t } = useTranslation();

  // Routing
  const navigate = useNavigate();
  const { courseId } = Route.useParams();
  const goBack = useCallback(() => {
    navigate({
      to: "/courses/$courseId",
      params: { courseId: courseId || "" },
    });
  }, []);

  // Queries
  const queryClient = useQueryClient();
  const [awaitingDetails, setAwaitingDetails] = useState(false);
  const { data: course, isPending: loadingBaseCourse } = useQuery(
    courseBaseByIdQueryOptions(queryClient, courseId),
  );

  // Forms
  const mainDetailsForm = useForm<MainDetailsFormValues>({
    mode: "onTouched",
    defaultValues: {
      courseName: "",
      courseDescription: "",
      authorDescription: "",
    },
    disabled: loadingBaseCourse,
    resolver: zodResolver(mainDetailsValidationSchema),
  });

  const demographicForm = useForm<DemographicFormValues>({
    defaultValues: {
      category: undefined,
      subcategory: undefined,
      payment: undefined,
    },
    resolver: zodResolver(demographicValidationSchema),
  });

  const { mutateAsync: basicEditCourse } = useBasicEditCourse();
  const saveChanges = useCallback(async () => {
    const { category, subcategory } = demographicForm.getValues();
    assert(!!course, "Course is required");

    const newCourseDetails = {
      courseId: parseInt(courseId) ?? "",
      course: {
        ...course,
        title: mainDetailsForm.getValues("courseName"),
        description: mainDetailsForm.getValues("courseDescription"),
        aboutTheAuthor: mainDetailsForm.getValues("authorDescription"),
        categoryId: subcategory ?? category,
      },
    } satisfies EditBasicCourseRequest;

    const res = await basicEditCourse(newCourseDetails);

    queryClient.invalidateQueries({
      queryKey: courseByIdQueryOptions(queryClient, courseId).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: courseBaseByIdQueryOptions(queryClient, courseId).queryKey,
    });

    navigate({
      to: "/courses/$courseId",
      params: { courseId: courseId || "" },
    });
  }, [course, setAwaitingDetails, mainDetailsForm, demographicForm]);

  const { mutateAsync: onSubmit, isPending: saving } = useMutation({
    mutationFn: saveChanges,
  });

  useEffect(() => {
    if (course) {
      mainDetailsForm.reset({
        courseName: course.title,
        courseDescription: course.description,
        authorDescription: course.aboutTheAuthor,
      });

      demographicForm.reset({
        category: course.category?.id,
        subcategory: course.subcategory?.id,
        payment: "0",
        accessBy: "all",
      });
    }
  }, [course]);

  return (
    <CourseDetailsForm
      mainDetailsForm={mainDetailsForm}
      demographicForm={demographicForm}
      onBack={goBack}
      submitButton={(isValid) => (
        <Button
          disabled={!isValid || awaitingDetails || saving}
          onClick={() => onSubmit()}
          size="large"
          variant="contained"
          color="primary"
        >
          {t("Action.SaveChanges")}
        </Button>
      )}
    />
  );
}
